/**
 * Sprint 2 — 6 Mrt 2026
 * BookingItemsEditor — multi-item editor voor boekingsformulier.
 *
 * Props:
 *   items                — array of BookingFormItem (mag [] zijn)
 *   onChange(items)      — parent state update
 *   currency             — factuur-valuta (voor waarde-weergave)
 *   wantsValueInvoice    — bool. Als true → value_per_unit verplicht + kolommen zichtbaar
 *   disabled             — bool
 *
 * Features:
 *   - Add / duplicate / remove
 *   - Live totalen (aantal, CBM, gewicht indien ingevuld, waarde indien waardefactuur actief)
 *   - Verplichte-veld validatie: description, quantity, length, width, height, unit
 *   - CBM per regel real-time berekend (cm/meter/inch)
 *   - Bewerkbaarheid mag nooit kapot: elke wijziging update direct
 */
import React, { useMemo, useCallback } from "react";
import { Plus, Copy, Trash2, Package } from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../../ui/select";

const UNITS = [
  { value: "cm", label: "cm" },
  { value: "meter", label: "meter" },
  { value: "inch", label: "inch" },
];

const INCH_TO_METER = 0.0254;

export function computeLineCbm(item) {
  const q = Number(item?.quantity || 0);
  const L = Number(item?.length || 0);
  const B = Number(item?.width || 0);
  const H = Number(item?.height || 0);
  if (q <= 0 || L <= 0 || B <= 0 || H <= 0) return 0;
  const unit = (item?.unit || "cm").toLowerCase();
  if (unit === "cm") return (L * B * H * q) / 1_000_000;
  if (unit === "inch") {
    return (L * INCH_TO_METER) * (B * INCH_TO_METER) * (H * INCH_TO_METER) * q;
  }
  return L * B * H * q;
}

export function computeTotals(items) {
  const totals = { pieces: 0, cbm: 0, weight: 0, value: 0 };
  for (const it of items || []) {
    const q = Number(it.quantity || 0);
    totals.pieces += q;
    totals.cbm += computeLineCbm(it);
    const w = Number(it.weight || 0);
    if (w > 0 && q > 0) totals.weight += w * q;
    const v = Number(it.value_per_unit || 0);
    if (v > 0 && q > 0) totals.value += v * q;
  }
  return totals;
}

function fmtCbm(n) {
  return Number(n || 0).toFixed(4);
}
function fmtWeight(n) {
  const v = Number(n || 0);
  return v > 0 ? v.toFixed(1) : "—";
}
function fmtCurrency(n, cur = "EUR") {
  const v = Number(n || 0);
  return v.toLocaleString("nl-NL", { style: "currency", currency: cur });
}

const EMPTY_ITEM = {
  description: "",
  quantity: 1,
  length: 0,
  width: 0,
  height: 0,
  unit: "cm",
  weight: null,
  value_per_unit: null,
  condition: "",
  hs_code: "",
  country_of_origin: "",
  note: "",
};

/** Retourneer een lijst van labels van ontbrekende verplichte velden. */
export function missingRequiredFields(it, wantsValueInvoice) {
  const missing = [];
  if (!(it?.description || "").trim()) missing.push("Omschrijving");
  if (!(Number(it?.quantity) > 0)) missing.push("Aantal");
  if (!(Number(it?.length) > 0)) missing.push("Lengte");
  if (!(Number(it?.width) > 0)) missing.push("Breedte");
  if (!(Number(it?.height) > 0)) missing.push("Hoogte");
  if (!it?.unit) missing.push("Eenheid");
  if (wantsValueInvoice && !(Number(it?.value_per_unit) > 0)) {
    missing.push("Waarde per stuk");
  }
  return missing;
}

/** Alles-geldig helper voor de parent (call in onSave). */
export function validateAllItems(items, wantsValueInvoice) {
  const errors = [];
  (items || []).forEach((it, i) => {
    const m = missingRequiredFields(it, wantsValueInvoice);
    if (m.length) errors.push({ index: i, missing: m });
  });
  return errors;
}

export default function BookingItemsEditor({
  items = [],
  onChange,
  currency = "EUR",
  wantsValueInvoice = false,
  disabled = false,
}) {
  const totals = useMemo(() => computeTotals(items), [items]);

  const setItem = useCallback((idx, next) => {
    onChange(items.map((it, i) => (i === idx ? next : it)));
  }, [items, onChange]);

  const addItem = useCallback(() => {
    onChange([...items, { ...EMPTY_ITEM }]);
  }, [items, onChange]);

  const duplicateItem = useCallback((idx) => {
    const source = items[idx];
    const clone = { ...source };
    const next = [...items];
    next.splice(idx + 1, 0, clone);
    onChange(next);
  }, [items, onChange]);

  const removeItem = useCallback((idx) => {
    onChange(items.filter((_, i) => i !== idx));
  }, [items, onChange]);

  return (
    <div className="space-y-2" data-testid="booking-items-editor">
      {(items || []).length === 0 && (
        <div className="text-sm text-gray-500 py-6 text-center border border-dashed border-gray-300 bg-gray-50"
             data-testid="booking-items-empty">
          Nog geen items toegevoegd. Klik op &quot;Item toevoegen&quot; om te beginnen.
        </div>
      )}

      {(items || []).map((it, idx) => {
        const missing = missingRequiredFields(it, wantsValueInvoice);
        const lineCbm = computeLineCbm(it);
        const lineValue = Number(it.value_per_unit || 0) * Number(it.quantity || 0);
        const isInvalid = missing.length > 0;
        return (
          <div
            key={idx}
            className={`border ${isInvalid ? "border-red-300 bg-red-50" : "border-gray-200 bg-white"} p-3`}
            data-testid={`booking-item-${idx}`}
          >
            {/* Header row */}
            <div className="flex items-center justify-between gap-2 mb-2">
              <div className="flex items-center gap-2 min-w-0">
                <div className={`h-6 w-6 flex items-center justify-center text-xs font-bold ${isInvalid ? "bg-red-200 text-red-900" : "bg-[#0A192F] text-white"}`}>
                  {idx + 1}
                </div>
                <div className="text-xs uppercase tracking-wide font-bold text-gray-700">
                  Item {idx + 1}
                </div>
                {isInvalid && (
                  <div className="text-[11px] text-red-700 font-bold ml-2"
                       data-testid={`booking-item-${idx}-missing`}>
                    Vul in: {missing.join(", ")}
                  </div>
                )}
              </div>
              <div className="flex gap-1">
                <Button type="button" variant="outline" size="sm"
                        onClick={() => duplicateItem(idx)}
                        disabled={disabled}
                        className="rounded-none text-xs h-7 px-2"
                        data-testid={`booking-item-${idx}-duplicate`}>
                  <Copy className="w-3 h-3 mr-1" /> Dupliceren
                </Button>
                <Button type="button" variant="outline" size="sm"
                        onClick={() => removeItem(idx)}
                        disabled={disabled}
                        className="rounded-none text-xs h-7 px-2 border-red-200 text-red-700 hover:bg-red-50"
                        data-testid={`booking-item-${idx}-remove`}>
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>

            {/* Omschrijving (breed) */}
            <div className="mb-2">
              <Label className="text-[10px] uppercase font-bold text-gray-600">
                Omschrijving *
              </Label>
              <Textarea
                value={it.description || ""}
                onChange={(e) => setItem(idx, { ...it, description: e.target.value })}
                placeholder="Bv. Doos met kleding, Barrel, Meubels, TV, Pallet met dozen…"
                rows={2}
                disabled={disabled}
                className="rounded-none mt-1 text-sm"
                data-testid={`booking-item-${idx}-description`}
              />
            </div>

            {/* Aantal + afmetingen + eenheid */}
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              <div>
                <Label className="text-[10px] uppercase font-bold text-gray-600">Aantal *</Label>
                <Input type="number" min="1" step="1"
                       value={it.quantity ?? ""}
                       onChange={(e) => setItem(idx, {
                         ...it, quantity: e.target.value === "" ? "" : Number(e.target.value),
                       })}
                       disabled={disabled}
                       className="rounded-none mt-1 text-sm"
                       data-testid={`booking-item-${idx}-quantity`} />
              </div>
              <div>
                <Label className="text-[10px] uppercase font-bold text-gray-600">Lengte *</Label>
                <Input type="number" min="0" step="0.01"
                       value={it.length ?? ""}
                       onChange={(e) => setItem(idx, {
                         ...it, length: e.target.value === "" ? "" : Number(e.target.value),
                       })}
                       disabled={disabled}
                       className="rounded-none mt-1 text-sm"
                       data-testid={`booking-item-${idx}-length`} />
              </div>
              <div>
                <Label className="text-[10px] uppercase font-bold text-gray-600">Breedte *</Label>
                <Input type="number" min="0" step="0.01"
                       value={it.width ?? ""}
                       onChange={(e) => setItem(idx, {
                         ...it, width: e.target.value === "" ? "" : Number(e.target.value),
                       })}
                       disabled={disabled}
                       className="rounded-none mt-1 text-sm"
                       data-testid={`booking-item-${idx}-width`} />
              </div>
              <div>
                <Label className="text-[10px] uppercase font-bold text-gray-600">Hoogte *</Label>
                <Input type="number" min="0" step="0.01"
                       value={it.height ?? ""}
                       onChange={(e) => setItem(idx, {
                         ...it, height: e.target.value === "" ? "" : Number(e.target.value),
                       })}
                       disabled={disabled}
                       className="rounded-none mt-1 text-sm"
                       data-testid={`booking-item-${idx}-height`} />
              </div>
              <div>
                <Label className="text-[10px] uppercase font-bold text-gray-600">Eenheid *</Label>
                <Select
                  value={it.unit || "cm"}
                  onValueChange={(v) => setItem(idx, { ...it, unit: v })}
                  disabled={disabled}
                >
                  <SelectTrigger className="rounded-none mt-1 text-sm h-9"
                                 data-testid={`booking-item-${idx}-unit`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITS.map((u) => (
                      <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Optioneel: gewicht, waarde per stuk (verplicht bij waardefactuur), staat */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
              <div>
                <Label className="text-[10px] uppercase font-bold text-gray-600">
                  Gewicht (kg)
                </Label>
                <Input type="number" min="0" step="0.1"
                       value={it.weight ?? ""}
                       onChange={(e) => setItem(idx, {
                         ...it, weight: e.target.value === "" ? null : Number(e.target.value),
                       })}
                       disabled={disabled}
                       placeholder="Optioneel"
                       className="rounded-none mt-1 text-sm"
                       data-testid={`booking-item-${idx}-weight`} />
              </div>
              {wantsValueInvoice && (
                <div>
                  <Label className="text-[10px] uppercase font-bold text-gray-600">
                    Waarde/stuk ({currency}) *
                  </Label>
                  <Input type="number" min="0" step="0.01"
                         value={it.value_per_unit ?? ""}
                         onChange={(e) => setItem(idx, {
                           ...it, value_per_unit: e.target.value === "" ? null : Number(e.target.value),
                         })}
                         disabled={disabled}
                         className="rounded-none mt-1 text-sm"
                         data-testid={`booking-item-${idx}-value-per-unit`} />
                </div>
              )}
              {wantsValueInvoice && (
                <div>
                  <Label className="text-[10px] uppercase font-bold text-gray-600">Staat</Label>
                  <Select
                    value={it.condition || ""}
                    onValueChange={(v) => setItem(idx, { ...it, condition: v })}
                    disabled={disabled}
                  >
                    <SelectTrigger className="rounded-none mt-1 text-sm h-9"
                                   data-testid={`booking-item-${idx}-condition`}>
                      <SelectValue placeholder="Optioneel" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nieuw">Nieuw</SelectItem>
                      <SelectItem value="gebruikt">Gebruikt</SelectItem>
                      <SelectItem value="persoonlijk_bezit">Persoonlijk bezit</SelectItem>
                      <SelectItem value="commercieel">Commercieel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {wantsValueInvoice && (
                <div>
                  <Label className="text-[10px] uppercase font-bold text-gray-600">HS-code</Label>
                  <Input type="text"
                         value={it.hs_code || ""}
                         onChange={(e) => setItem(idx, { ...it, hs_code: e.target.value })}
                         placeholder="Optioneel"
                         disabled={disabled}
                         className="rounded-none mt-1 text-sm"
                         data-testid={`booking-item-${idx}-hs-code`} />
                </div>
              )}
              {wantsValueInvoice && (
                <div className="sm:col-span-2">
                  <Label className="text-[10px] uppercase font-bold text-gray-600">
                    Land van oorsprong
                  </Label>
                  <Input type="text"
                         value={it.country_of_origin || ""}
                         onChange={(e) => setItem(idx, { ...it, country_of_origin: e.target.value })}
                         placeholder="Optioneel"
                         disabled={disabled}
                         className="rounded-none mt-1 text-sm"
                         data-testid={`booking-item-${idx}-origin`} />
                </div>
              )}
              <div className={wantsValueInvoice ? "sm:col-span-2" : "sm:col-span-3"}>
                <Label className="text-[10px] uppercase font-bold text-gray-600">Notitie</Label>
                <Input type="text"
                       value={it.note || ""}
                       onChange={(e) => setItem(idx, { ...it, note: e.target.value })}
                       placeholder="Optioneel — extra info"
                       disabled={disabled}
                       className="rounded-none mt-1 text-sm"
                       data-testid={`booking-item-${idx}-note`} />
              </div>
            </div>

            {/* Line totaal bar */}
            <div className="mt-2 pt-2 border-t border-gray-200 flex flex-wrap gap-x-4 gap-y-1 text-xs">
              <span className="text-gray-600">
                CBM: <span className="font-mono font-bold text-[#0A192F]"
                          data-testid={`booking-item-${idx}-cbm`}>{fmtCbm(lineCbm)}</span>
              </span>
              {Number(it.weight) > 0 && (
                <span className="text-gray-600">
                  Gewicht totaal: <span className="font-mono">{fmtWeight(Number(it.weight) * Number(it.quantity || 0))} kg</span>
                </span>
              )}
              {wantsValueInvoice && lineValue > 0 && (
                <span className="text-gray-600">
                  Regelwaarde: <span className="font-mono font-bold text-[#0A192F]"
                                     data-testid={`booking-item-${idx}-line-value`}>
                    {fmtCurrency(lineValue, currency)}
                  </span>
                </span>
              )}
            </div>
          </div>
        );
      })}

      {/* Add-knop */}
      <Button
        type="button" variant="outline"
        onClick={addItem} disabled={disabled}
        className="rounded-none text-xs w-full"
        data-testid="booking-items-add"
      >
        <Plus className="w-3 h-3 mr-1" /> Item toevoegen
      </Button>

      {/* Live totalen */}
      <div className="bg-[#0A192F] text-white p-3 flex flex-wrap gap-x-6 gap-y-2 text-sm"
           data-testid="booking-items-totals">
        <span className="flex items-center gap-1">
          <Package className="w-3.5 h-3.5" />
          <strong className="font-mono" data-testid="totals-pieces">{Math.round(totals.pieces)}</strong>
          <span className="text-gray-300 text-xs ml-1">totaal aantal</span>
        </span>
        <span>
          <strong className="font-mono" data-testid="totals-cbm">{fmtCbm(totals.cbm)}</strong>
          <span className="text-gray-300 text-xs ml-1">CBM</span>
        </span>
        {totals.weight > 0 && (
          <span>
            <strong className="font-mono" data-testid="totals-weight">{fmtWeight(totals.weight)} kg</strong>
            <span className="text-gray-300 text-xs ml-1">gewicht totaal</span>
          </span>
        )}
        {wantsValueInvoice && totals.value > 0 && (
          <span>
            <strong className="font-mono" data-testid="totals-value">{fmtCurrency(totals.value, currency)}</strong>
            <span className="text-gray-300 text-xs ml-1">totale waarde</span>
          </span>
        )}
      </div>
    </div>
  );
}
